import i18n from '@benzinga/translate';
import { formatAfterHours, formatEST, formatNumericDateTime } from '@benzinga/date-utils';
import { AFTER_MARKET, NO_TRADING, PRE_MARKET } from '@benzinga/quotes-manager/constants';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { QuoteSessionType } from '@benzinga/quotes-manager';

interface TimeLabels {
  timestampClose: string;
  timestampEth: string;
}

export const isExtendedHours = (type: QuoteSessionType) =>
  type === PRE_MARKET || type === AFTER_MARKET || type === NO_TRADING;

interface ExtendedPriceData {
  change: number;
  changePercent: number;
  price: number;
  time: number;
  volume: number;
}

export const extendedPriceData = (quote: DelayedQuote): ExtendedPriceData | null => {
  if (quote.close !== undefined) {
    const ethChange = quote.lastTradePrice - quote.close;
    const ethChangePercent = (ethChange / quote.close) * 100;
    return {
      change: ethChange,
      changePercent: ethChangePercent,
      price: quote.close,
      time: quote.ethTime,
      volume: quote.ethVolume,
    };
  }
  return null;
};

type TranslateFn = (key: string, options?: { ns?: string }) => string;

export const getTimeLabels = (
  quote: DelayedQuote,
  sessionType?: QuoteSessionType,
  options?: { longDate?: boolean; isRealTime?: boolean },
  translateFn?: TranslateFn,
): TimeLabels => {
  const t = translateFn ?? ((key: string, opts?: { ns?: string }) => i18n.t(key, opts));
  const formatter = options?.isRealTime ? formatNumericDateTime : formatEST;
  const isExtended = sessionType ? isExtendedHours(sessionType) : false;
  let timestampClose =
    typeof quote.lastTradeTime === 'number' && quote.lastTradeTime > 0
      ? `${t('Quote.Price.last-update', { ns: 'quote' })}: ${formatter(quote.lastTradeTime, options?.longDate)} `
      : '';
  let timestampEth = '';
  if (isExtended && quote.lastTradeTime) {
    if (sessionType === PRE_MARKET) {
      const previousCloseDate = quote.previousCloseDate ? new Date(quote.previousCloseDate).getTime() : null;
      timestampClose = previousCloseDate
        ? `${t('Quote.Price.at-close', { ns: 'quote' })}: ${formatEST(previousCloseDate, true)} EST`
        : '';
    } else {
      const closeDate = quote.closeDate ? new Date(quote.closeDate).getTime() : null;
      timestampClose = closeDate
        ? `${t('Quote.Price.at-close', { ns: 'quote' })}: ${formatAfterHours(closeDate, options?.longDate)} EST`
        : '';
    }
    if (quote.ethPrice !== undefined) {
      let timestampEthPrefix = t('Quote.Market.pre-market', { ns: 'quote' });
      if (sessionType === AFTER_MARKET || sessionType === NO_TRADING) {
        timestampEthPrefix = t('Quote.Market.after-hours', { ns: 'quote' });
      }
      if (sessionType === PRE_MARKET) {
        timestampEth =
          typeof quote.askTime === 'number'
            ? `${timestampEthPrefix}: ${formatEST(quote.askTime, options?.longDate)} EST`
            : '';
      } else {
        timestampEth =
          typeof quote.ethTime === 'number'
            ? `${timestampEthPrefix}: ${formatEST(quote.ethTime, options?.longDate)} EST`
            : '';
      }
    }
  }
  if (isExtended && !quote.lastTradePrice && !quote.lastTradeTime && quote.close) {
    const closeDate = quote.closeDate ? new Date(quote.closeDate).getTime() : null;
    timestampClose = closeDate
      ? `${t('Quote.Price.at-close', { ns: 'quote' })}: ${formatAfterHours(closeDate, options?.longDate)} EST`
      : '';
  }
  return {
    timestampClose,
    timestampEth,
  };
};

const calculateChange = (n1: number, n2: number): [number, number, number] => {
  const rawChange = n1 - n2;
  const change = parseFloat(rawChange.toFixed(2));
  const changePercent = n2 !== 0 ? parseFloat(((change / n2) * 100).toFixed(2)) : 0;
  return [n1, change, changePercent];
};

export const regularSessionQuote = (close?: number | null, previousClose?: number | null, price?: number | null) => {
  if (close && close > 0.0 && previousClose && previousClose > 0.0) {
    return calculateChange(close as number, previousClose as number);
  } else if (price && price > 0.0 && previousClose && previousClose > 0.0) {
    return calculateChange(price as number, previousClose as number);
  } else {
    return [undefined, undefined, undefined];
  }
};
